class WallpaperCalculator extends HTMLElement {
  constructor() {
    super();
    this.currentUnit = 'inches';
    this.rollWidth = 52;
    this.rollLength = 360;
  }

  connectedCallback() {
    this.sectionId = this.querySelector('.wallpaper-calculator').dataset.sectionId;
    this.heightInput = this.querySelector('#wall-height');
    this.widthInput = this.querySelector('#wall-width');
    this.sqftInput = this.querySelector('#total-sqft');
    this.extraCheckbox = this.querySelector('#include-extra');

    // Try multiple selectors to find the quantity input
    this.quantityInput = document.querySelector(`#Quantity-${this.sectionId}`) ||
                        document.querySelector('#quantity') ||
                        document.querySelector('input[name="quantity"]') ||
                        document.querySelector('.quantity-wrapper input[type="number"]');

    this.dimensionsPanel = this.querySelector('[data-panel="dimensions"]');
    this.sqftPanel = this.querySelector('[data-panel="sqft"]');

    this.rollWidthDisplay = this.querySelector('.calculator__roll-width-display');
    this.coverageDisplay = this.querySelector('.calculator__coverage-display');
    this.rollsDisplay = this.querySelector('.calculator__rolls-display');

    this.setupEventListeners();
    this.detectRollWidth();
    this.calculate();
  }

  setupEventListeners() {
    // Tab switching
    this.querySelectorAll('.calculator__tab').forEach(tab => {
      tab.addEventListener('click', (e) => this.switchTab(e.target.dataset.tab));
    });

    // Unit toggle
    this.querySelectorAll('.calculator__unit-toggle').forEach(toggle => {
      toggle.addEventListener('click', (e) => this.switchUnit(e.target.dataset.unit));
    });

    // Input changes
    [this.heightInput, this.widthInput, this.sqftInput].forEach(input => {
      if (input) {
        input.addEventListener('input', () => this.calculate());
      }
    });

    if (this.extraCheckbox) {
      this.extraCheckbox.addEventListener('change', () => {
        this.calculate();
      });
    }

    // Sample button - search in document since it might be in a separate block
    const sampleButton = document.querySelector('.calculator__sample-button');
    if (sampleButton) {
      sampleButton.addEventListener('click', () => {
        this.addSampleToCart();
      });
    }

    // Listen for Roll Width variant changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name*="Roll Width"]')) {
        console.log('Roll Width radio changed to:', e.target.value);
        this.detectRollWidth();
        this.calculate();
      }
    });

    // Also listen for any variant changes
    const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
    if (variantSelects) {
      const observer = new MutationObserver(() => {
        this.detectRollWidth();
        this.calculate();
      });
      observer.observe(variantSelects, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-selected-variant']
      });
    }
  }

  switchTab(tab) {
    this.querySelectorAll('.calculator__tab').forEach(t => t.classList.remove('calculator__tab--active'));
    this.querySelectorAll('.calculator__panel').forEach(p => p.classList.remove('calculator__panel--active'));

    this.querySelector(`[data-tab="${tab}"]`).classList.add('calculator__tab--active');
    this.querySelector(`[data-panel="${tab}"]`).classList.add('calculator__panel--active');

    this.calculate();
  }

  switchUnit(unit) {
    const oldUnit = this.currentUnit;
    this.currentUnit = unit;

    // Update toggle buttons
    this.querySelectorAll('.calculator__unit-toggle').forEach(toggle => {
      toggle.classList.toggle('calculator__unit-toggle--active', toggle.dataset.unit === unit);
    });

    // Convert existing values when switching units
    if (this.heightInput && this.heightInput.value) {
      const currentHeight = parseFloat(this.heightInput.value);
      if (currentHeight && !isNaN(currentHeight)) {
        if (oldUnit === 'inches' && unit === 'feet') {
          // Convert inches to feet
          this.heightInput.value = (currentHeight / 12).toFixed(2);
        } else if (oldUnit === 'feet' && unit === 'inches') {
          // Convert feet to inches
          this.heightInput.value = (currentHeight * 12).toFixed(0);
        }
      }
    }

    if (this.widthInput && this.widthInput.value) {
      const currentWidth = parseFloat(this.widthInput.value);
      if (currentWidth && !isNaN(currentWidth)) {
        if (oldUnit === 'inches' && unit === 'feet') {
          // Convert inches to feet
          this.widthInput.value = (currentWidth / 12).toFixed(2);
        } else if (oldUnit === 'feet' && unit === 'inches') {
          // Convert feet to inches
          this.widthInput.value = (currentWidth * 12).toFixed(0);
        }
      }
    }

    // Update placeholder text
    if (this.heightInput) {
      this.heightInput.placeholder = unit === 'inches' ? 'e.g., 96' : 'e.g., 8';
    }
    if (this.widthInput) {
      this.widthInput.placeholder = unit === 'inches' ? 'e.g., 120' : 'e.g., 10';
    }

    // Update label text to show current unit
    const unitLabels = this.querySelectorAll('.calculator__unit-label');
    unitLabels.forEach(label => {
      label.textContent = unit === 'inches' ? '(inches)' : '(feet)';
    });

    this.calculate();
  }

  detectRollWidth() {
    const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);

    if (variantSelects) {
      // Try to get from checked radio button
      const rollWidthRadio = variantSelects.querySelector('input[name*="Roll Width"]:checked');
      if (rollWidthRadio) {
        const value = rollWidthRadio.value;
        console.log('Detected Roll Width from radio:', value);

        // Extract number from value (e.g., "52&quot;" or "26&quot;" or "52" or "26")
        const match = value.match(/(\d+)/);
        if (match) {
          this.rollWidth = parseInt(match[1]);
          console.log('Set rollWidth to:', this.rollWidth);
        }
      }

      // Try to get from variant data
      const variantScript = variantSelects.querySelector('script[data-selected-variant]');
      if (variantScript && !rollWidthRadio) {
        try {
          const variant = JSON.parse(variantScript.textContent);
          console.log('Current variant:', variant);

          // Check all options for Roll Width
          [variant.option1, variant.option2, variant.option3].forEach((option, index) => {
            if (option && (option.includes('52') || option.includes('26'))) {
              const match = option.match(/(\d+)/);
              if (match) {
                this.rollWidth = parseInt(match[1]);
                console.log(`Found Roll Width in option${index + 1}:`, this.rollWidth);
              }
            }
          });
        } catch (e) {
          console.error('Error parsing variant:', e);
        }
      }
    }

    console.log('Final rollWidth:', this.rollWidth);
  }

  calculate() {
    let wallHeightInches = 0;
    let wallWidthInches = 0;
    let rollsNeeded = 0;

    // Determine which panel is active
    const isDimensionsActive = this.dimensionsPanel?.classList.contains('calculator__panel--active');

    if (isDimensionsActive) {
      // Get dimensions
      let height = parseFloat(this.heightInput?.value) || 0;
      let width = parseFloat(this.widthInput?.value) || 0;

      // Convert to inches if needed
      if (this.currentUnit === 'feet') {
        height = height * 12;
        width = width * 12;
      }

      wallHeightInches = height;
      wallWidthInches = width;
    } else {
      // Use square footage
      const sqft = parseFloat(this.sqftInput?.value) || 0;

      // For sqft calculation, we need to estimate dimensions
      // Assuming standard 8ft (96") ceiling height
      wallHeightInches = 96;
      if (sqft > 0) {
        wallWidthInches = (sqft * 144) / wallHeightInches;
      }
    }

    // Calculate rolls using proper wallpaper formula
    if (wallHeightInches > 0 && wallWidthInches > 0) {
      // Number of strips needed (round up)
      const stripsNeeded = Math.ceil(wallWidthInches / this.rollWidth);

      // Strips per roll (round down to be safe)
      const stripsPerRoll = Math.floor(this.rollLength / wallHeightInches);

      // Rolls needed
      if (stripsPerRoll > 0) {
        rollsNeeded = Math.ceil(stripsNeeded / stripsPerRoll);
      }

      console.log('Calculation:', {
        wallHeightInches,
        wallWidthInches,
        rollWidth: this.rollWidth,
        rollLength: this.rollLength,
        stripsNeeded,
        stripsPerRoll,
        rollsNeeded
      });
    }

    // Apply 15% extra - add extra ROLLS (not sq ft)
    let extraRolls = 0;
    if (this.extraCheckbox?.checked && rollsNeeded > 0) {
      extraRolls = Math.ceil(rollsNeeded * 0.15);
      rollsNeeded = rollsNeeded + extraRolls;
    }

    // Update displays
    if (this.rollWidthDisplay) {
      this.rollWidthDisplay.textContent = `${this.rollWidth}"`;
    }

    if (this.coverageDisplay) {
      const coveragePerRoll = (this.rollWidth * this.rollLength) / 144;
      this.coverageDisplay.textContent = `${coveragePerRoll.toFixed(1)} sq ft`;
    }

    if (this.rollsDisplay) {
      this.rollsDisplay.textContent = rollsNeeded;
    }

    // Update quantity input
    if (this.quantityInput) {
      this.quantityInput.value = rollsNeeded;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  getSelectedVariantProperties() {
    const properties = {};

    // Get all variant options from the product form - try multiple selectors
    const variantPicker = document.querySelector('variant-picker') ||
                         document.querySelector(`variant-selects[data-section="${this.sectionId}"]`) ||
                         document.querySelector('variant-selects');

    if (variantPicker) {
      console.log('Found variant picker:', variantPicker);

      // Get all option selectors (fieldsets and selects)
      const optionSelectors = variantPicker.querySelectorAll('.option-selector, fieldset[data-option]');

      optionSelectors.forEach(selector => {
        const optionName = selector.dataset.option || selector.querySelector('legend')?.textContent?.trim().replace(':', '');
        console.log('Processing option:', optionName);

        // Get checked radio or selected option
        const checkedInput = selector.querySelector('input[type="radio"]:checked') ||
                           selector.querySelector('select');

        if (checkedInput) {
          const value = checkedInput.value;
          console.log(`${optionName}:`, value);

          // Map the option name to property
          if (optionName) {
            if (optionName.toLowerCase().includes('color') || optionName.toLowerCase().includes('colour')) {
              properties['Color'] = value;
            } else if (optionName.toLowerCase().includes('material')) {
              properties['Material'] = value;
            } else if (optionName.toLowerCase().includes('roll') && optionName.toLowerCase().includes('width')) {
              properties['Roll Width'] = value;
            } else if (optionName.toLowerCase().includes('width')) {
              properties['Roll Width'] = value;
            }
          }
        }
      });

      // Fallback: try from variant data if still missing
      if (Object.keys(properties).length < 3) {
        const variantScript = variantPicker.querySelector('script[type="application/json"]');
        if (variantScript) {
          try {
            const data = JSON.parse(variantScript.textContent);
            const variants = data.variants;

            // Get current URL params to find selected variant
            const urlParams = new URLSearchParams(window.location.search);
            const variantId = urlParams.get('variant');

            if (variantId && variants) {
              const currentVariant = variants.find(v => v.id == variantId);
              if (currentVariant) {
                if (!properties['Color'] && currentVariant.option1) properties['Color'] = currentVariant.option1;
                if (!properties['Material'] && currentVariant.option2) properties['Material'] = currentVariant.option2;
                if (!properties['Roll Width'] && currentVariant.option3) properties['Roll Width'] = currentVariant.option3;
              }
            } else if (variants && variants.length > 0) {
              // Use first available variant as fallback
              const firstVariant = variants[0];
              if (!properties['Color'] && firstVariant.option1) properties['Color'] = firstVariant.option1;
              if (!properties['Material'] && firstVariant.option2) properties['Material'] = firstVariant.option2;
              if (!properties['Roll Width'] && firstVariant.option3) properties['Roll Width'] = firstVariant.option3;
            }
          } catch (e) {
            console.error('Error parsing variant data:', e);
          }
        }
      }
    }

    console.log('Final properties:', properties);
    return properties;
  }

  async addSampleToCart() {
    const sampleButton = document.querySelector('.calculator__sample-button');
    const sampleVariantId = sampleButton?.dataset.sampleVariantId;

    if (!sampleVariantId) {
      console.error('Sample variant ID not found');
      return;
    }

    // Get selected variant properties
    const properties = this.getSelectedVariantProperties();

    try {
      // Add sample to cart using Shopify Cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: sampleVariantId,
          quantity: 1,
          properties: properties
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add sample to cart');
      }

      const result = await response.json();
      console.log('Sample added to cart:', result);

      // Store the cart item key for later removal
      if (result.key) {
        this.sampleCartKey = result.key;
      }

      // Update cart count
      this.updateCartCount(1);

      // Show success message
      this.showSampleAddedMessage();

      // Trigger cart drawer/notification if available
      const cartDrawerOpener = document.querySelector('cart-drawer-opener');
      if (cartDrawerOpener) {
        cartDrawerOpener.click();
      }
    } catch (error) {
      console.error('Error adding sample to cart:', error);
    }
  }

  async removeSampleFromCart() {
    try {
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Find the sample item in the cart
      const sampleVariantId = this.extraCheckbox.dataset.sampleVariantId;
      const sampleItem = cart.items.find(item => item.variant_id == sampleVariantId);

      if (sampleItem) {
        // Remove the item using the change endpoint
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: sampleItem.key,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error('Failed to remove sample from cart');
        }

        console.log('Sample removed from cart');

        // Update cart count
        this.updateCartCount(-1);

        // Show removal message
        this.showSampleRemovedMessage();
      }
    } catch (error) {
      console.error('Error removing sample from cart:', error);
    }
  }

  updateCartCount(change) {
    // Find cart count element - try multiple selectors
    const cartCount = document.querySelector('.cart-link__count') ||
                     document.querySelector('.cart-count-bubble span') ||
                     document.querySelector('[data-cart-count]');

    if (cartCount) {
      const currentCount = parseInt(cartCount.textContent) || 0;
      const newCount = Math.max(0, currentCount + change);
      cartCount.textContent = newCount;
    }
  }

  showSampleAddedMessage() {
    // Create success alert
    const alertDiv = document.createElement('div');
    alertDiv.className = 'sample-success-alert';
    alertDiv.style.cssText = `
      position: fixed;
      top: 2rem;
      right: 2rem;
      z-index: 9999;
      background-color: #10b981;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      opacity: 0;
      transform: translateX(400px);
      transition: all 0.3s ease;
    `;
    alertDiv.innerHTML = `
      <div style="display: flex; align-items: center; gap: 0.75rem;">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="currentColor"/>
        </svg>
        <span style="font-size: 0.95rem; font-weight: 500;">Sample added to cart!</span>
      </div>
    `;

    document.body.appendChild(alertDiv);

    // Show the alert
    setTimeout(() => {
      alertDiv.style.opacity = '1';
      alertDiv.style.transform = 'translateX(0)';
    }, 10);

    // Hide and remove after 3 seconds
    setTimeout(() => {
      alertDiv.style.opacity = '0';
      alertDiv.style.transform = 'translateX(400px)';
      setTimeout(() => {
        alertDiv.remove();
      }, 300);
    }, 3000);
  }

  showSampleRemovedMessage() {
    // Create removal alert
    const alertDiv = document.createElement('div');
    alertDiv.className = 'sample-removed-alert';
    alertDiv.style.cssText = `
      position: fixed;
      top: 2rem;
      right: 2rem;
      z-index: 9999;
      background-color: #ef4444;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      opacity: 0;
      transform: translateX(400px);
      transition: all 0.3s ease;
    `;
    alertDiv.innerHTML = `
      <div style="display: flex; align-items: center; gap: 0.75rem;">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="currentColor"/>
        </svg>
        <span style="font-size: 0.95rem; font-weight: 500;">Sample removed from cart!</span>
      </div>
    `;

    document.body.appendChild(alertDiv);

    // Show the alert
    setTimeout(() => {
      alertDiv.style.opacity = '1';
      alertDiv.style.transform = 'translateX(0)';
    }, 10);

    // Hide and remove after 3 seconds
    setTimeout(() => {
      alertDiv.style.opacity = '0';
      alertDiv.style.transform = 'translateX(400px)';
      setTimeout(() => {
        alertDiv.remove();
      }, 300);
    }, 3000);
  }
}

customElements.define('wallpaper-calculator', WallpaperCalculator);
